'use client';

/**
 * /me/data — self-service Data Subject Rights (PDPL Art. 15 + 18).
 *
 * Single-page UI:
 *   1. User enters email + picks Export or Delete
 *   2. We POST /api/me/request-data-action — anti-enumeration, always 200
 *   3. User clicks magic link in email → arrives back here at /me/data?token=...
 *   4. Token-confirm path POSTs to /api/me/confirm
 *      - delete: shows the audit summary (scans/photos/escalations deleted)
 *      - export: shows job id, polls /api/me/job/<id> until status=completed,
 *                then offers the download (data: URI)
 *
 * No accounts, no logins, no cookies. The magic-link IS the auth.
 */

import { Suspense, useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { TurnstileWidget } from '@/components/TurnstileWidget';

const API_BASE = 'https://api.mysophia.tech';

type Phase =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent' }
  | { kind: 'confirming' }
  | { kind: 'delete-done'; summary: Record<string, number> }
  | { kind: 'export-pending'; jobId: string }
  | { kind: 'export-done'; downloadUrl: string }
  | { kind: 'error'; message: string };

function MeDataContent() {
  const sp = useSearchParams();
  const tokenInUrl = sp.get('token');
  const actionInUrl = sp.get('action') as 'export' | 'delete' | null;

  const [email, setEmail] = useState('');
  const [action, setAction] = useState<'export' | 'delete'>('export');
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  // v3.7 W3.5 — Turnstile token (null until widget produces one;
  // null-tolerant in dev when sitekey not configured).
  const [cfToken, setCfToken] = useState<string | null>(null);
  const handleCfToken = useCallback((t: string | null) => setCfToken(t), []);

  // If the user landed here from the email magic-link, run confirm.
  // Audit P0-2 (2026-05-04): immediately strip the token from the URL
  // bar so it doesn't sit in browser history / open-tabs / shoulder-
  // surfing range. The magic-link is single-use server-side, but a
  // 15-min-fresh token left in history is still a leak surface.
  useEffect(() => {
    if (!tokenInUrl) return;
    const confirmAction = actionInUrl === 'delete' ? 'delete' : 'export';
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.delete('token');
      url.searchParams.delete('action');
      window.history.replaceState({}, '', url.toString());
    }
    setPhase({ kind: 'confirming' });
    void confirm(tokenInUrl, confirmAction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function requestAction(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!email.trim()) return;
    setPhase({ kind: 'sending' });
    try {
      const res = await fetch(`${API_BASE}/api/me/request-data-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), action, cfToken }),
      });
      const json = (await res.json()) as { success: boolean; error?: string };
      if (!json.success) {
        setPhase({ kind: 'error', message: json.error ?? 'Request failed' });
        return;
      }
      setPhase({ kind: 'sent' });
    } catch (err) {
      setPhase({ kind: 'error', message: (err as Error).message });
    }
  }

  async function confirm(token: string, act: 'export' | 'delete'): Promise<void> {
    try {
      const res = await fetch(`${API_BASE}/api/me/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, action: act }),
      });
      const json = (await res.json()) as {
        success: boolean;
        error?: string;
        action?: 'export' | 'delete';
        summary?: Record<string, number>;
        jobId?: string;
      };
      if (!json.success) {
        setPhase({ kind: 'error', message: json.error ?? 'Confirmation failed' });
        return;
      }
      if (json.action === 'delete' && json.summary) {
        setPhase({ kind: 'delete-done', summary: json.summary });
      } else if (json.action === 'export' && json.jobId) {
        setPhase({ kind: 'export-pending', jobId: json.jobId });
        void pollExport(json.jobId);
      }
    } catch (err) {
      setPhase({ kind: 'error', message: (err as Error).message });
    }
  }

  async function pollExport(jobId: string): Promise<void> {
    for (let i = 0; i < 60; i++) {
      // up to 5 min total
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const res = await fetch(`${API_BASE}/api/me/job/${jobId}`);
        const json = (await res.json()) as {
          success: boolean;
          job?: { status: string; downloadUrl?: string; failureReason?: string };
        };
        if (!json.success || !json.job) continue;
        if (json.job.status === 'completed' && json.job.downloadUrl) {
          setPhase({ kind: 'export-done', downloadUrl: json.job.downloadUrl });
          return;
        }
        if (json.job.status === 'failed') {
          setPhase({
            kind: 'error',
            message: json.job.failureReason ?? 'Export job failed',
          });
          return;
        }
      } catch {
        // transient — keep polling
      }
    }
    setPhase({ kind: 'error', message: 'Export is taking longer than expected. Try again later.' });
  }

  return (
    <main className="min-h-screen bg-ink-900 text-fog-100">
      <div className="mx-auto max-w-2xl px-6 py-20">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-sm text-fog-500 hover:text-gold-soft mb-10 transition"
        >
          ← Back to home
        </Link>
        <h1 className="font-serif text-5xl mb-4">Your data</h1>
        <p className="text-fog-400 mb-10">
          Export or delete every piece of data Sophia holds linked to your email. No login required —
          confirmation goes to your inbox. PDPL Article 15 + 18 compliant.
        </p>

        {phase.kind === 'idle' && (
          <form onSubmit={requestAction} className="space-y-6">
            <div>
              <label
                htmlFor="dsr-email"
                className="block text-[10px] tracking-[0.3em] uppercase text-gold-soft font-semibold mb-2"
              >
                Your email
              </label>
              <input
                id="dsr-email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full px-4 py-3 rounded-lg bg-ink-800 border border-fog-700 text-fog-100 placeholder:text-fog-600 focus:border-gold-soft focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-gold-soft font-semibold mb-3">
                Action
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className={`block cursor-pointer rounded-lg border p-4 transition ${action === 'export' ? 'bg-ink-800 border-gold-soft/40' : 'bg-ink-900 border-fog-700 hover:border-fog-500'}`}>
                  <input
                    type="radio"
                    name="action"
                    value="export"
                    checked={action === 'export'}
                    onChange={() => setAction('export')}
                    className="sr-only"
                  />
                  <div className="font-bold text-fog-100 text-sm mb-1">Export my data</div>
                  <div className="text-fog-400 text-xs leading-relaxed">
                    A JSON package of every record we hold, plus 24-hour download links to any
                    photos you opted to store.
                  </div>
                </label>
                <label className={`block cursor-pointer rounded-lg border p-4 transition ${action === 'delete' ? 'bg-ink-800 border-rose/40' : 'bg-ink-900 border-fog-700 hover:border-fog-500'}`}>
                  <input
                    type="radio"
                    name="action"
                    value="delete"
                    checked={action === 'delete'}
                    onChange={() => setAction('delete')}
                    className="sr-only"
                  />
                  <div className="font-bold text-fog-100 text-sm mb-1">Delete everything</div>
                  <div className="text-fog-400 text-xs leading-relaxed">
                    Permanently remove all scans, photos, escalations, and waitlist entries linked
                    to this email. Irreversible.
                  </div>
                </label>
              </div>
            </div>

            <TurnstileWidget action="me-data-request" onToken={handleCfToken} />

            <button
              type="submit"
              className="px-6 py-3 rounded-full bg-gradient-to-r from-gold-deep to-gold text-obsidian-900 font-bold text-sm tracking-wide hover:shadow-glow-gold transition"
            >
              {action === 'export' ? 'Send me an export link' : 'Send me a deletion link'}
            </button>
          </form>
        )}

        {phase.kind === 'sending' && (
          <p className="text-fog-300">Sending magic link…</p>
        )}

        {phase.kind === 'sent' && (
          <div className="rounded-lg bg-ink-800 border border-fog-700 p-6">
            <h2 className="font-serif text-2xl text-fog-100 mb-3">Check your email</h2>
            <p className="text-fog-300 mb-2">
              If <strong>{email}</strong> is associated with any Sophia data, you&apos;ll receive
              a confirmation link in the next minute or two. The link expires in 15 minutes and
              can only be used once.
            </p>
            <p className="text-fog-500 text-xs mb-4">
              Didn&apos;t arrive? Check your spam folder. Still nothing after 5 minutes? It may be
              that the email isn&apos;t in our system — we don&apos;t reveal that information for
              your privacy.
            </p>
            {/* Audit P2-9 (2026-05-04): give the user a way to fix a typo'd
                email without using browser back. */}
            <button
              type="button"
              onClick={() => setPhase({ kind: 'idle' })}
              className="text-xs tracking-[0.2em] uppercase text-fog-300 hover:text-fog-100 underline transition"
            >
              Use a different email
            </button>
          </div>
        )}

        {phase.kind === 'confirming' && (
          <p className="text-fog-300">Confirming your request…</p>
        )}

        {phase.kind === 'delete-done' && (
          <div className="rounded-lg bg-emerald/10 border border-emerald/30 p-6">
            <h2 className="font-serif text-2xl text-emerald mb-3">Deletion complete</h2>
            <p className="text-fog-300 mb-4">
              Every record we held linked to your email has been removed.
            </p>
            <ul className="text-fog-300 text-sm space-y-1 list-disc ps-6">
              <li>Scans removed: {phase.summary.scansDeleted ?? 0}</li>
              <li>Photos removed: {phase.summary.photosDeleted ?? 0}</li>
              <li>Escalations removed: {phase.summary.escalationsDeleted ?? 0}</li>
              <li>Waitlist entries removed: {phase.summary.waitlistDeleted ?? 0}</li>
            </ul>
            <p className="text-fog-500 text-xs mt-4">
              An audit row was kept in our internal logs for compliance purposes; it does not
              contain your email plaintext.
            </p>
          </div>
        )}

        {phase.kind === 'export-pending' && (
          <div className="rounded-lg bg-ink-800 border border-fog-700 p-6">
            <h2 className="font-serif text-2xl text-fog-100 mb-3">Preparing your export…</h2>
            <p className="text-fog-300">
              Job id <code className="text-fog-100">{phase.jobId.slice(0, 8)}…</code> running.
              This usually takes under a minute. Don&apos;t close this page.
            </p>
          </div>
        )}

        {phase.kind === 'export-done' && (
          <div className="rounded-lg bg-emerald/10 border border-emerald/30 p-6">
            <h2 className="font-serif text-2xl text-emerald mb-3">Your export is ready</h2>
            <p className="text-fog-300 mb-4">
              Download below. The link is valid for 24 hours.
            </p>
            <a
              href={phase.downloadUrl}
              download="sophia-export.json"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-soft text-obsidian-900 text-sm font-bold hover:shadow-glow-gold transition"
            >
              Download JSON
            </a>
          </div>
        )}

        {phase.kind === 'error' && (
          <div className="rounded-lg bg-rose/10 border border-rose/30 p-6">
            <h2 className="font-serif text-xl text-rose mb-2">Something went wrong</h2>
            <p className="text-fog-300 text-sm">{phase.message}</p>
            <button
              onClick={() => setPhase({ kind: 'idle' })}
              className="mt-4 text-sm text-gold-soft underline hover:text-gold"
            >
              Try again
            </button>
          </div>
        )}
      </div>
    </main>
  );
}

export default function MeDataPage() {
  return (
    <Suspense fallback={<main className="min-h-screen bg-ink-900" />}>
      <MeDataContent />
    </Suspense>
  );
}
